import React, { useEffect, useRef, useState } from "react";
import {
  FaCheck,
  FaInfoCircle,
  FaTimes,
  FaCopy,
  FaTelegramPlane,
} from "react-icons/fa";
import { FaXTwitter, FaDiscord } from "react-icons/fa6";
import { toast } from "react-toastify";
import { httpReauest } from "../../utils/httpRequest";
import { toPng } from "html-to-image";

const ShareTask = ({ user, reward, getData }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [twitterLink, setTwitterLink] = useState("");
  const [twitterLoading, settwitterLoading] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const shareRef = useRef();

  // State to hold the generated image data URL for iOS display
  const [iosImageDataUrl, setIosImageDataUrl] = useState(null);

  const handleCheckClick = () => {
    setModalOpen(true);
  };

  const fetchUserInfo = async () => {
    const userStorage = JSON.parse(localStorage.getItem("user"));
    const res = await httpReauest("GET", `/user/share-card/${userStorage._id}`);
    setUserInfo(res.data.data);
  };

  const handleDownloadImage = async () => {
    try {
      // Increased delay to ensure full rendering on iOS, addressing WebKit bug
      await new Promise((resolve) => setTimeout(resolve, 750));

      const element = shareRef.current;
      if (!element) {
        toast.error("Share card element not found.");
        return;
      }

      const dataUrl = await toPng(element, {
        cacheBust: true,
        skipFonts: true,
        // Allows processing of cross-origin images
        allowCrossOriginData: true,
      });

      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

      if (isIOS) {
        // Instead of window.open, set the image data URL to be displayed in a modal
        setIosImageDataUrl(dataUrl);
        toast.info("Image ready. Long-press to save."); // Simplified message
      } else {
        // For other systems, direct download
        const link = document.createElement("a");
        link.download = "filetrust-share.png";
        link.href = dataUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("Image downloaded!"); // Confirmation for other platforms
      }
    } catch (err) {
      console.error("❌ html-to-image error:", err);
      toast.error("Failed to generate image.");
    }
  };

  const copyMessage = `🚀 I'm officially participating in the FileTrust Airdrop campaign! 🎯 Join the mission and earn amazing rewards 💰 by completing simple tasks. 🔐 Track your progress and share your score with the world! 🌍 Check your status now 👉 https://airdrop.filetrust.io`;

  const copyText = () => {
    navigator.clipboard.writeText(copyMessage);
    toast.success("Copied to clipboard!");
  };

  const handlePlatformClick = async (platform) => {
    if (platform === "telegram") {
      if (!user.telegramUsername) {
        toast.error("You haven't joined our Telegram group yet.");
        return;
      }
      await httpReauest("POST", "/user/share-card/telegram")
        .then(() => {
          setModalOpen(false);
          toast.success("Successful!");
          getData();
        })
        .catch((err) => {
          toast.error(err.response?.data?.message);
        });
    }
    if (platform === "discord") {
      if (!user.discordInfo) {
        toast.error("You haven't joined our Discord server yet.");
        return;
      }
      await httpReauest("POST", "/user/share-card/discord")
        .then(() => {
          setModalOpen(false);
          toast.success("Successful!");
          getData();
        })
        .catch((err) => {
          toast.error(err.response?.data?.message);
        });
    }
    setSelectedPlatform(platform);
  };

  useEffect(() => {
    if (isModalOpen) fetchUserInfo();
  }, [isModalOpen]);

  const formatDate = (date) => {
    if (!(date instanceof Date) || isNaN(date))
      throw new Error("Invalid date provided");
    const day = date.getDate();
    const month = date
      .toLocaleString("default", { month: "short" })
      .toUpperCase();
    const year = date.getFullYear();
    return `${day} ${month}, ${year}`;
  };

  return (
    <div
      className={`${
        user?.shareCard?.isDoes && "order-last opacity-40"
      } flex justify-between gap-3 items-center relative border-b py-2 pb-4 text-white`}
    >
      <span className="flex items-center gap-2 lg:gap-4">
        <img
          src="https://www.filetrust.io/img/Logo_white.svg"
          className="w-8 lg:w-14"
          alt="Logo"
        />
        <span>
          <h4 className="font-bold text-[14px] lg:text-[28px] lg:-mb-2 flex items-center gap-2">
            Share Your Status
            <FaInfoCircle
              className="text-blue-400 cursor-pointer"
              onClick={() => {
                !user?.shareCard?.isDoes && setIsModalOpen(true);
              }}
              title="Help"
            />
            👈
          </h4>
          <span className="font-bold text-[16px] lg:text-[28px] flex gap-1 items-center">
            <img src="/gold.png" className="w-6 lg:w-8" alt="GOLD" />+{reward}
          </span>
        </span>
      </span>

      <div className="flex flex-col items-end gap-2">
        <div className="flex gap-2">
          {user?.shareCard?.isDoes ? (
            <button
              disabled
              className="start-button px-4 lg:px-8 py-2 bg-[#02006A] shadow shadow-white/40 rounded-full text-[14px] lg:text-[24px] flex gap-2 items-center"
            >
              <FaCheck color="green" /> Claimed
            </button>
          ) : (
            <button
              onClick={handleCheckClick}
              className="start-button px-4 lg:px-8 py-2 bg-[#02006A] shadow shadow-white/40 rounded-full text-[14px] lg:text-[24px]"
            >
              Check
            </button>
          )}
        </div>
      </div>

      {/* Share platform modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-[#1e293b] p-6 rounded-xl w-full max-w-md text-white shadow-xl relative m-2">
            <button
              onClick={() => setModalOpen(false)}
              className="absolute right-3 top-3 text-white"
            >
              <FaTimes />
            </button>
            <h3 className="text-2xl font-semibold mb-1 text-center">
              Where did you share it?
            </h3>
            <p className="text-lg text-gray-200 mb-4 text-center">
              Choose the platform you shared it on.
            </p>
            <div className="flex justify-center gap-4 mb-4">
              {/* هر آیکون داخل یک دکمه مجزا */}
              {/* <button
                onClick={() => handlePlatformClick("twitter")}
                className="p-3 rounded-full bg-blue-500 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-200 flex items-center justify-center"
                aria-label="Share on X (Twitter)"
              >
                <FaXTwitter className="text-[30px]" />
              </button> */}
              <button
                onClick={() => handlePlatformClick("telegram")}
                className="p-3 rounded-full bg-blue-500 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-200 flex items-center justify-center"
                aria-label="Share on Telegram"
              >
                <FaTelegramPlane className="text-[30px]" />
              </button>
              <button
                onClick={() => handlePlatformClick("discord")}
                className="p-3 rounded-full bg-purple-500 hover:bg-purple-600 focus:outline-none focus:ring-2 focus:ring-purple-400 transition-all duration-200 flex items-center justify-center"
                aria-label="Share on Discord"
              >
                <FaDiscord className="text-[30px]" />
              </button>
            </div>
            {selectedPlatform === "twitter" && (
              <>
                <label className="block text-sm mb-1">
                  Paste your tweet link here
                </label>
                <input
                  type="text"
                  value={twitterLink}
                  onChange={(e) => setTwitterLink(e.target.value)}
                  placeholder="https://twitter.com/..."
                  className="w-full px-4 py-2 rounded-lg bg-gray-800 border border-white/10 text-white mb-4"
                />
                <button
                  className="w-full bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded"
                  disabled={twitterLoading}
                  onClick={async () => {
                    settwitterLoading(true);
                    await httpReauest(
                      "POST",
                      "/user/share-card/twitter",
                      { twitterLink },
                      {}
                    )
                      .then((res) => {
                        setModalOpen(false);
                        toast.success("Successful!");
                        getData();
                      })
                      .catch((err) => {
                        toast.error(err.response?.data?.message);
                        settwitterLoading(false);
                      });
                  }}
                >
                  {twitterLoading ? "Loading..." : "Submit"}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Info Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 ">
          <div className="bg-[#1e293b] rounded-xl max-w-[370px] p-4 text-white relative max-h-[90vh] overflow-auto">
            <button
              onClick={() => {
                setIsModalOpen(false);
                setIosImageDataUrl(null); // Clear image data when closing modal
              }}
              className="absolute right-3 top-3 text-white"
            >
              <FaTimes />
            </button>
            <h2 className="text-xl font-bold mb-3">Your Shareable Card</h2>
            <div className="bg-[#111827] border border-white/10 rounded-lg p-1 mb-4 text-center">
              {userInfo ? (
                <>
                  <div
                    ref={shareRef}
                    className="rounded-[16px] bg-[#0E171D] p-2 gap-2 flex flex-row flex-wrap w-full justify-between items-center relative max-w-[370px] mx-auto"
                  >
                    <span className="flex flex-row  w-auto justify-center gap-2 items-center mt-2 sm:mt-0 min-w-0">
                      {userInfo?.user && (
                        <span className="relative flex-shrink-0">
                          <img
                            src={`/profile/${userInfo?.user?.gender?.toLowerCase()}/${
                              userInfo?.user?.level > 6
                                ? 6
                                : userInfo?.user?.level
                            }.jpg`}
                            className="min-w-20 max-w-20"
                            alt="User Profile"
                            crossOrigin="anonymous"
                          />
                          {userInfo?.user?.loginStreak?.streak >= 90 ? (
                            <img
                              src="/streak/90.png"
                              className="absolute right-0 bottom-6 w-8"
                              alt="Strak"
                              crossOrigin="anonymous"
                            />
                          ) : userInfo?.user?.loginStreak?.streak >= 60 ? (
                            <img
                              src="/streak/60.png"
                              className="absolute right-0 bottom-6 w-8"
                              alt="Strak"
                              crossOrigin="anonymous"
                            />
                          ) : userInfo?.user?.loginStreak?.streak >= 30 ? (
                            <img
                              src="/streak/30.png"
                              className="absolute right-0 bottom-6 w-8"
                              alt="Strak"
                              crossOrigin="anonymous"
                            />
                          ) : userInfo?.user?.loginStreak?.streak >= 14 ? (
                            <img
                              src="/streak/14.png"
                              className="absolute right-0 bottom-6 w-8"
                              alt="Strak"
                              crossOrigin="anonymous"
                            />
                          ) : userInfo?.user?.loginStreak?.streak >= 7 ? (
                            <img
                              src="/streak/7.png"
                              className="absolute right-0 bottom-6 w-8"
                              alt="Strak"
                              crossOrigin="anonymous"
                            />
                          ) : null}
                        </span>
                      )}

                      <span class="text-left min-w-0">
                        <h2 className="font-bold text-[18px] min-w-0">
                          {userInfo?.user?.name}
                        </h2>
                        <p className="text-[10px] min-w-0">
                          Joined{" "}
                          {userInfo?.user
                            ? formatDate(new Date(userInfo?.user?.createdAt))
                            : null}
                        </p>
                      </span>
                    </span>

                    <span class="flex-shrink-0 mt-2 sm:mt-0 min-w-0">
                      <h4 className="text-[14px] text-center font-bold min-w-0">
                        # {userInfo?.userRank}
                      </h4>
                      <h4 className="text-[12px] text-center font-bold min-w-0">
                        TRUST POINT
                      </h4>
                      <h4 className="text-[16px] text-center font-bold min-w-0">
                        {userInfo?.user?.balance}
                      </h4>
                    </span>
                    <div class=" w-full justify-center flex flex-col items-center">
                      <p className="text-[10px] text-center sm:text-left min-w-0">
                        airdrop.filetrust.io
                      </p>
                      <p className="text-[10px] text-center sm:text-left flex items-center justify-center sm:justify-start gap-1 min-w-0">
                        <FaXTwitter className="min-w-3 min-h-3" /> @FileTrustio
                      </p>
                    </div>
                  </div>
                </>
              ) : (
                <p>Loading...</p>
              )}
            </div>

            {/* Conditionally render the image for iOS users */}
            {iosImageDataUrl && (
              <div className="my-4 text-center">
                <p className="text-lg mb-2 text-blue-300 font-bold">
                  Hold image to save
                </p>
                <img
                  src={iosImageDataUrl}
                  alt="Shareable Card"
                  className="w-full h-auto max-h-[300px] object-contain border border-gray-700 rounded-lg mx-auto"
                  style={{ display: "block" }} // Ensure it displays correctly
                />
              </div>
            )}

            <div className="mb-3">
              <label className="text-sm text-gray-300 mb-1 block">
                Your Share Text
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={copyMessage}
                  readOnly
                  className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg border border-white/10 pr-10"
                />
                <button
                  onClick={copyText}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-white hover:text-green-400"
                >
                  <FaCopy />
                </button>
              </div>
            </div>
            <div className="flex gap-3 mb-2">
              <button
                onClick={handleDownloadImage}
                className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded"
              >
                Download Image
              </button>
            </div>
            <div className="bg-yellow-800/20 border-l-4 border-yellow-400 text-yellow-100 p-4 rounded-lg mt-4">
              <p className="text-sm leading-relaxed font-semibold">
                ⚠️ To complete the task, you{" "}
                <span className="text-yellow-300">
                  must share the exact image and message provided above
                </span>{" "}
                on **Telegram**, or **Discord**.
                <br />
                <span className="text-yellow-300 font-bold">
                  If either the text or the image is missing, your submission
                  will not be approved.
                </span>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShareTask;
